'use client'

import { useEffect, useRef, useState } from 'react'

import { waitForFreestarConsent } from '@/app/components/freestar/consent'
import type { FreestarOpportunity } from '@/app/components/freestar/types'

import { AD_SHIELD_SCRIPT, AD_SHIELD_SCRIPT_ID, FREESTAR_HEAD_SCRIPT_ID, FREESTAR_HEAD_SCRIPT_SRC } from './payload'

export function shouldRenderAdShield(opportunity?: FreestarOpportunity, consentAllowed = false): boolean {
    return opportunity?.status === 'ready' && consentAllowed
}

type ScriptTarget = Pick<Document, 'createElement' | 'getElementById'> & {
    head: Pick<HTMLHeadElement, 'appendChild'>
}

export function injectHeadScript(
    { scriptId, scriptContent, scriptSrc }: { scriptId: string; scriptContent?: string; scriptSrc?: string },
    target: ScriptTarget = document
): boolean {
    if (!scriptId || (!scriptContent && !scriptSrc) || target.getElementById(scriptId)) {
        return false
    }

    const script = target.createElement('script')
    script.id = scriptId
    script.setAttribute('data-cfasync', 'false')
    script.type = 'text/javascript'
    if (scriptSrc) {
        script.src = scriptSrc
    } else if (scriptContent) {
        script.text = scriptContent
    }
    target.head.appendChild(script)
    return true
}

export default function AdShieldScript({ opportunity }: { opportunity?: FreestarOpportunity }) {
    const [consentAllowed, setConsentAllowed] = useState(false)
    const injected = useRef(false)

    useEffect(() => {
        if (opportunity?.status !== 'ready') {
            setConsentAllowed(false)
            return
        }

        let active = true
        waitForFreestarConsent().then((consent) => {
            if (active) {
                setConsentAllowed(consent.status === 'allowed')
            }
        })

        return () => {
            active = false
        }
    }, [opportunity])

    useEffect(() => {
        if (
            injected.current ||
            !shouldRenderAdShield(opportunity, consentAllowed) ||
            (!AD_SHIELD_SCRIPT && !FREESTAR_HEAD_SCRIPT_SRC)
        ) {
            return
        }

        const injectedAdShield = injectHeadScript({ scriptId: AD_SHIELD_SCRIPT_ID, scriptContent: AD_SHIELD_SCRIPT })
        const injectedFreestarHead = injectHeadScript({
            scriptId: FREESTAR_HEAD_SCRIPT_ID,
            scriptSrc: FREESTAR_HEAD_SCRIPT_SRC,
        })
        injected.current = injectedAdShield || injectedFreestarHead
    }, [consentAllowed, opportunity])

    return null
}
